import React, { useEffect } from 'react';

/**
 * Hides the webpack-dev-server / CRA error overlay so our Error Boundary fallback is visible.
 * Uses a style tag + MutationObserver to hide the overlay as soon as it's injected.
 */
function HideDevOverlay() {
  useEffect(() => {
    const OVERLAY_IDS = ['webpack-dev-server-client-overlay', 'webpack-dev-server-client-overlay-div'];
    const hide = (el) => {
      if (el && el.style) el.style.setProperty('display', 'none', 'important');
    };
    const apply = () => {
      OVERLAY_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (el) hide(el);
      });
    };
    const style = document.createElement('style');
    style.id = 'error-boundary-hide-overlay';
    style.textContent = OVERLAY_IDS.map((id) => `#${id} { display: none !important; }`).join('\n');
    if (!document.getElementById(style.id)) document.head.appendChild(style);
    apply();
    const observer = new MutationObserver(() => apply());
    observer.observe(document.body, { childList: true, subtree: true });
    const intervals = [10, 50, 150, 400].map((ms) => setTimeout(apply, ms));
    return () => {
      observer.disconnect();
      intervals.forEach(clearTimeout);
      style.remove();
    };
  }, []);
  return null;
}

/**
 * Error Boundary: catches JavaScript errors in the child tree and shows a fallback UI
 * instead of a blank screen. Wraps the app in index.js.
 */
class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught:', error, errorInfo);
    }
  }

  handleRetry = () => {
    const style = document.getElementById('error-boundary-hide-overlay');
    if (style) style.remove();
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <>
          {process.env.NODE_ENV === 'development' && <HideDevOverlay />}
          <div
            style={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              fontFamily: 'system-ui, -apple-system, sans-serif',
              background: '#f8f9fa',
              color: '#1a1a1a',
              textAlign: 'center',
              position: 'relative',
              zIndex: 99999,
            }}
          >
            <h1 style={{ fontSize: '1.5rem', marginBottom: 8 }}>Something went wrong</h1>
            <p style={{ marginBottom: 24, maxWidth: 400 }}>
              We're sorry. The app hit an error. Please try again or refresh the page.
            </p>
            <button
              type="button"
              onClick={this.handleRetry}
              style={{
                padding: '12px 24px',
                fontSize: 16,
                fontWeight: 600,
                color: '#fff',
                background: '#18442A',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre
                style={{
                  marginTop: 24,
                  padding: 16,
                  background: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: 8,
                  fontSize: 12,
                  textAlign: 'left',
                  overflow: 'auto',
                  maxWidth: '100%',
                }}
              >
                {this.state.error.toString()}
              </pre>
            )}
          </div>
        </>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
